<template>
  <div class="hris-page">
    <div class="hris-page-inner">
      <div class="hris-page-header">
        <span class="hris-page-eyebrow">Leaves</span>
        <h1 class="hris-page-title">Special Leave Applications</h1>
        <p class="hris-page-subtitle">Search an employee to file a special leave application on their behalf.</p>
      </div>

      <q-form ref="vForm" @submit="onSubmitSearch" class="hris-toolbar">
        <div class="hris-form-field" style="flex: 1 1 280px; max-width: 400px;">
          <q-input :rules="[formRules.required]" outlined v-model="searchModel" dense placeholder="Search employee here..." color="green">
            <template v-slot:append>
              <q-icon name="fa fa-search" />
            </template>
          </q-input>
        </div>
        <q-btn unelevated class="hris-q-primary" label="Search" type="submit"></q-btn>
      </q-form>

      <div class="hris-panel">
        <q-table title="Search Result" flat :data="searchResultData" :columns="searchResultColumns" row-key="credit_id" :hide-bottom="searchResultData.length < 5" :loading="loading">
          <q-tr slot="body" slot-scope="props" :props="props" class="cursor-pointer" @click="openLeaveAppDialog(props.row.name,props.row.credit_id,props.row.employee_id,props.row.gender,props.row.ml_note,props.row.pl_note,props.row.mc_note,props.row.sp_note,props.row.sp_availed)">
            <q-td key="employee_id" :props="props">{{ props.row.employee_id }}</q-td>
            <q-td key="name" :props="props">{{ props.row.name }}</q-td>
            <q-td key="position" :props="props">{{ props.row.position }}</q-td>
            <q-td key="department" :props="props">{{ props.row.department }}</q-td>
            <q-td key="maternity_leave" :props="props">
              <q-avatar :icon="props.row.ml_availed" font-size="20px" :text-color="props.row.ml_bg_color"></q-avatar>
              <q-tooltip anchor="top middle" self="center middle" content-class="bg-grey-9 text-white" :offset="[10, 10]">
                <strong>{{ props.row.ml_note }}</strong>
              </q-tooltip>
            </q-td>
            <q-td key="paternity_leave" :props="props">
              <q-avatar :icon="props.row.pl_availed" font-size="20px" :text-color="props.row.pl_bg_color"></q-avatar>
              <q-tooltip anchor="top middle" self="center middle" content-class="bg-grey-9 text-white" :offset="[10, 10]">
                <strong>{{ props.row.pl_note }}</strong>
              </q-tooltip>
            </q-td>
            <q-td key="magna_carta_leave" :props="props">
              <q-avatar :icon="props.row.mc_availed" font-size="20px" :text-color="props.row.mc_bg_color"></q-avatar>
              <q-tooltip anchor="top middle" self="center middle" content-class="bg-grey-9 text-white" :offset="[10, 10]">
                <strong>{{ props.row.mc_note }}</strong>
              </q-tooltip>
            </q-td>
            <q-td key="solo_parent_leave" :props="props">
              <q-avatar :icon="props.row.is_sp_availed" font-size="20px" :text-color="props.row.sp_bg_color"></q-avatar>
              <q-tooltip anchor="top middle" self="center middle" content-class="bg-grey-9 text-white" :offset="[10, 10]">
                <strong>{{ props.row.sp_note }}</strong>
              </q-tooltip>
            </q-td>
          </q-tr>
          <template v-slot:no-data="{ message }">
            <div class="hris-empty full-width">{{ message }}</div>
          </template>
        </q-table>
      </div>

      <q-dialog v-model="leaveAppDialog" persistent transition-show="scale" transition-hide="scale">
        <div class="hris-modal">
          <div class="hris-modal-header">
            <div>
              <h2 class="hris-modal-title">Special Leave Application</h2>
              <p class="hris-modal-subtitle">{{ employeeNameID }}</p>
            </div>
          </div>
          <div class="hris-modal-body">
            <q-form>
              <div class="hris-form-row">
                <div class="hris-form-field" style="flex-basis: 100%;">
                  <label class="hris-form-label">Leave Type</label>
                  <q-select v-model="form.leaveType" :options="leaveOptions" outlined dense color="green"></q-select>
                </div>
              </div>
              <div class="hris-form-row">
                <div class="hris-form-field">
                  <label class="hris-form-label">Inclusive Date From</label>
                  <q-input v-model="dateFromInput" outlined dense color="green" readonly>
                    <template v-slot:append>
                      <q-icon name="fa fa-calendar-alt" class="cursor-pointer">
                        <q-popup-proxy ref="qDateFromProxy" transition-show="flip-down" transition-hide="flip-up">
                          <q-date v-model="form.selectedDateFrom" @input="$refs.qDateFromProxy.hide()" :options="startDate" today-btn color="green" minimal></q-date>
                        </q-popup-proxy>
                      </q-icon>
                    </template>
                  </q-input>
                </div>
                <div class="hris-form-field">
                  <label class="hris-form-label">Inclusive Date To</label>
                  <q-input v-model="dateToInput" outlined dense color="green" readonly>
                    <template v-slot:append>
                      <q-icon name="fa fa-calendar-alt" class="cursor-pointer">
                        <q-popup-proxy ref="qDateToProxy" transition-show="flip-down" transition-hide="flip-up">
                          <q-date v-model="form.selectedDateTo" @input="$refs.qDateToProxy.hide()" :options="endDate" today-btn color="green" minimal></q-date>
                        </q-popup-proxy>
                      </q-icon>
                    </template>
                  </q-input>
                </div>
              </div>
              <div class="hris-form-row">
                <div class="hris-form-field" style="flex-basis: 100%;">
                  <label class="hris-form-label">Reason</label>
                  <q-input v-model="form.leaveReason" outlined dense color="green" maxlength="200" autogrow clearable bottom-slots counter hint="Max 200 characters"></q-input>
                </div>
              </div>
            </q-form>
          </div>
          <div class="hris-modal-footer">
            <q-btn unelevated class="hris-q-outline" label="Close" v-close-popup no-caps></q-btn>
            <q-btn unelevated class="hris-q-primary" @click="submitLeaveApplication" :loading="loading" label="Submit" no-caps>
              <template v-slot:loading>
                <q-spinner color="green" class="on-left"></q-spinner>
                Sending...
              </template>
            </q-btn>
          </div>
        </div>
      </q-dialog>
    </div>
  </div>
</template>
<script>
import axios from 'axios';
import { date } from 'quasar';
import { exportFile } from 'quasar';
let today = Date.now();
let dateToday = date.formatDate(today, 'MM-DD-YYYY-hhmmA');
export default {
  name: 'SpecialLeaveApplications',
  data() {
    return {
      dateToday,
      employeeNameID: '',
      leaveAppDialog: false,
      loading: false,
      searchModel: null,
      searchResultData: [],
      searchResultColumns: [{
          name: 'employee_id',
          field: 'employee_id',
          label: 'Employee ID',
          style: 'width: 120px',
          align: 'center',
          headerStyle: 'width: 120px'
        },
        {
          name: 'name',
          field: 'name',
          label: 'Name',
          style: 'width: 250px',
          align: 'center',
          headerStyle: 'width: 250px'
        },
        {
          name: 'position',
          field: 'position',
          label: 'Position',
          style: 'width: 250px',
          align: 'center',
          headerStyle: 'width: 250px'
        },
        {
          name: 'department',
          field: 'department',
          label: 'Department',
          style: 'width: 200px',
          align: 'center',
          headerStyle: 'width: 200px'
        },
        {
          name: 'maternity_leave',
          field: 'maternity_leave',
          label: 'Maternity Leave',
          style: 'width: 100px',
          align: 'center',
          headerStyle: 'width: 100px; padding: none'
        },
        {
          name: 'paternity_leave',
          field: 'paternity_leave',
          label: 'Paternity Leave',
          style: 'width: 100px',
          align: 'center',
          headerStyle: 'width: 100px'
        },
        {
          name: 'magna_carta_leave',
          field: 'magna_carta_leave',
          label: 'Magna Carta Leave',
          style: 'width: 100px',
          align: 'center',
          headerStyle: 'width: 100px'
        },
        {
          name: 'solo_parent_leave',
          field: 'solo_parent_leave',
          label: 'Solo Parent Leave',
          style: 'width: 100px',
          align: 'center',
          headerStyle: 'width: 100px'
        }
      ],
      gender: null,
      ml_availed: null,
      ml_availment: 'fa fa-check',
      ml_avail_color: 'green-10',
      pl_availed: null,
      pl_availment: 'fa fa-check',
      pl_avail_color: 'green-10',
      mc_availed: null,
      mc_availment: 'fa fa-check',
      mc_avail_color: 'green-10',
      is_sp_availed: null,
      sp_availed: null,
      sp_availment: 'fa fa-check',
      sp_avail_color: 'green-10',
      leaveOptions: [],
      sp_validation: null,
      form: {
        employee_id: null,
        leaveType: 'Please select type of Leave',
        selectedDateFrom: null,
        selectedDateTo: null,
        leaveReason: null
      },
    }
  },
  methods: {
    onSubmitSearch: function() {
      this.employeeNameID = '';
      this.loading = true;
      this.$refs.vForm.validate();
      axios.create({
          baseURL: this.apiUrl,
          headers: {
            'Authorization': `Bearer ${this.token}`
          }
        })
        .get('/leave/credits/search-employee-credits', {
          params: {
            keyword: this.searchModel
          }
        })
        .then((res) => {
          this.loading = false;
          this.searchResultData = [];
          for (let emp of res.data.data) {
            this.searchResultData.push({
              credit_id: emp.credit_id,
              employee_id: emp.employee_id,
              name: this.toTitleCase(emp.name),
              gender: emp.gender,
              position: emp.position,
              department: emp.department,
              ml_availed: emp.ml_availed,
              ml_bg_color: emp.ml_bg_color,
              ml_note: emp.note_ml,
              pl_availed: emp.pl_availed,
              pl_bg_color: emp.pl_bg_color,
              pl_note: emp.note_pl,
              mc_availed: emp.mc_availed,
              mc_bg_color: emp.mc_bg_color,
              mc_note: emp.note_mc,
              sp_availed: emp.sp_availed,
              is_sp_availed: emp.is_sp_availed,
              sp_bg_color: emp.sp_bg_color,
              sp_note: emp.note_sp
            });
          }
          console.log(this.searchResultData);
        })
    },
    openLeaveAppDialog: function(rowName, rowID, empID, gender, noteML, notePL, noteMC, noteSP, spAvailed) {
      this.form.employee_id = rowID;
      this.leaveOptions = [];
      this.form.leaveType = 'Please select type of Leave';
      this.form.selectedDateFrom = null;
      this.form.selectedDateTo = null;
      this.form.leaveReason = null;
      this.employeeNameID = rowName + ' - ' + empID;
      this.sp_validation = spAvailed;
      if (gender == 'F') {
        if (noteML == 'Application Available') {
          this.leaveOptions.push('Maternity Leave')
        }
        if (noteMC == 'Application Available') {
          this.leaveOptions.push('Magna Carta Leave')
        }
        if (noteSP == 'Application Available') {
          this.leaveOptions.push('Solo Parent Leave')
        }
      } else {
        if (notePL == 'Application Available') {
          this.leaveOptions.push('Paternity Leave')
        }
        if (noteSP == 'Application Available') {
          this.leaveOptions.push('Solo Parent Leave')
        }
      }
      this.leaveAppDialog = true;
    },
    startDate: function(date) {
      return date >= this.dateToday;
    },
    endDate: function(date) {
      if (this.form.selectedDateFrom == null) {
        return date >= this.dateToday;
      } else {
        return date >= this.form.selectedDateFrom;
      }
    },
    submitLeaveApplication: function() {
      const dateTots = date.getDateDiff(this.form.selectedDateTo, this.form.selectedDateFrom, 'days') + 1;
      let remSP = 7 - this.sp_validation;
      console.log(remSP);
      console.log(this.sp_validation);
      null
      if (this.form.selectedDateFrom == null || this.form.selectedDateTo == null ||
        this.form.leaveReason == null || this.form.leaveReason == '') {
        this.$q.notify({
          color: 'red-6',
          textColor: 'white',
          icon: 'fa fa-exclamation-circle',
          message: 'Please fill-in all necessary details.'
        });
      } else if (this.form.selectedDateFrom > this.form.selectedDateTo) {
        this.$q.notify({
          color: 'red-6',
          textColor: 'white',
          icon: 'fa fa-exclamation-circle',
          message: 'Please check the date. "Date From" is greater than "Date To".'
        });
      } else if (this.form.leaveType == 'Solo Parent Leave' && dateTots > remSP) {
        this.$q.notify({
          color: 'red-6',
          textColor: 'white',
          icon: 'fa fa-exclamation-circle',
          message: 'Insufficient Leave Credits. Available SP Leave Credit: ' + remSP
        });
      } else {
        this.loading = true;
        this.form.selectedDateFrom = date.formatDate(this.form.selectedDateFrom, 'YYYY-MM-DD');
        this.form.selectedDateTo = date.formatDate(this.form.selectedDateTo, 'YYYY-MM-DD');
        let formData = new FormData();
        formData.append('form_data', JSON.stringify(this.form));
        axios.create({
          baseURL: this.apiUrl,
          headers: {
            'Authorization': `Bearer ${this.token}`
          }
        }).post('/leave/special-application', formData).then((res) => {
          setTimeout(() => {
            this.loading = false;
            this.$q.notify({
              color: 'green-7',
              textColor: 'white',
              icon: 'fa fa-shipping-fast',
              message: this.form.leaveType + ' Application Submitted'
            });
            var self = this;
            this.form.leaveType = 'Please select type of Leave from the list';
            this.form.selectedDateFrom = null;
            this.form.selectedDateTo = null;
            this.form.leaveReason = null;
            this.onSubmitSearch();
            this.leaveAppDialog = false;
          }, 1000);
        });
      }
    },
  },
  computed: {
    dateFromInput: function() {
      if (this.form.selectedDateFrom == null) {
        return 'Please select a date...';
      } else {
        let d = new Date(this.form.selectedDateFrom);
        return date.formatDate(d, 'dddd, MMMM DD, YYYY');
      }
    },
    dateToInput: function() {
      if (this.form.selectedDateTo == null) {
        return 'Please select a date...';
      } else {
        let d = new Date(this.form.selectedDateTo);
        return date.formatDate(d, 'dddd, MMMM DD, YYYY');
      }
    }
  }
}
</script>
<style lang="css" scoped>
</style>
